import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { getLanguageFromLocale, type Locale } from '@seek/melways-sites';

import { getLocale, getZone } from 'src/core/config/globalStates';
import {
  FEATURED_THREADS_PAGE_SIZE,
  PAGE_SIZE,
} from 'src/core/domain/constants';
import { ThreadError } from 'src/core/domain/errors';
import {
  Platform,
  type ThreadDetailQuery,
  type ThreadDetailQueryVariables,
  ThreadDetailDocument,
  type SeekMaxThreadCommentsQueryVariables,
  type SeekMaxThreadCommentsQuery,
  SeekMaxThreadCommentsDocument,
  type CategorySeekMaxThreadsQueryVariables,
  type CategorySeekMaxThreadsQuery,
  CategorySeekMaxThreadsDocument,
  type DeleteSeekMaxThreadMutation,
  type DeleteSeekMaxThreadMutationVariables,
  DeleteSeekMaxThreadDocument,
  type CreateSeekMaxThreadMutationVariables,
  type CreateSeekMaxThreadAttachmentInput,
  CreateSeekMaxThreadDocument,
  type FeaturedSeekMaxThreadsV3QueryVariables,
  type FeaturedSeekMaxThreadsV3Query,
  FeaturedSeekMaxThreadsV3Document,
} from 'src/generated/gql/graphql';
import {
  type CategoryThreadsResult,
  type Thread,
  type Comment,
  generateGroupThreads,
  generateThreadDetail,
  generateComments,
  generateFeaturedThreads,
  type FeaturedThreadsResult,
} from 'src/mapper/threadMapper';
import { client as csrClient } from 'src/modules/apollo/apolloClient';
import logger from 'src/modules/logger/logger';
import type { CareerHubLanguage, ConfigStateContext } from 'src/types';
import type { ThreadCreationStatus } from 'src/types/threads';

export interface fetchThreadDetailsParams {
  id: string;
  isAuthenticated: boolean;
  pageSize: number;
  config: ConfigStateContext;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const fetchThreadDetails = async ({
  id,
  isAuthenticated,
  pageSize,
  config,
  client = csrClient,
}: fetchThreadDetailsParams): Promise<Thread> => {
  const { locale, zone, language, timezone } = config;
  try {
    const variables: ThreadDetailQueryVariables = {
      locale: locale || getLocale(),
      zone: zone || getZone(),
      cursor: 0,
      pageSize,
      timezone,
      isAuthenticated,
      platform: Platform.Web,
      languageCode: getLanguageFromLocale(getLocale() as Locale),
      threadId: id,
    };

    logger.info('fetchThreadDetails', variables);

    const { data } = await client.query<
      ThreadDetailQuery,
      ThreadDetailQueryVariables
    >({
      query: ThreadDetailDocument,
      variables,
    });
    return generateThreadDetail(data, language as CareerHubLanguage);
  } catch (error) {
    logger.error({ error }, 'fetchThreadDetails');
    throw error;
  }
};

export interface fetchCommentsParams {
  threadId: string;
  pageSize: number;
  isAuthenticated: boolean;
  cursor?: number;
  config: Pick<ConfigStateContext, 'locale' | 'timezone'>;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const fetchComments = async ({
  threadId,
  pageSize,
  isAuthenticated,
  cursor = 0,
  config,
  client = csrClient,
}: fetchCommentsParams): Promise<Comment[]> => {
  const { locale, timezone } = config;
  try {
    const variables: SeekMaxThreadCommentsQueryVariables = {
      locale: locale || getLocale(),
      cursor,
      pageSize,
      timezone,
      isAuthenticated,
      threadId,
    };

    logger.info('fetchComments', variables);

    const { data } = await client.query<
      SeekMaxThreadCommentsQuery,
      SeekMaxThreadCommentsQueryVariables
    >({
      query: SeekMaxThreadCommentsDocument,
      variables,
    });

    return generateComments(data);
  } catch (error) {
    logger.error({ error }, 'fetchComments');
    throw error;
  }
};

export interface fetchFeaturedThreadsParams {
  cursor: number;
  pageSize?: number;
  isAuthenticated?: boolean;
  config: Pick<ConfigStateContext, 'locale' | 'language' | 'zone' | 'timezone'>;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const fetchFeaturedThreads = async ({
  cursor,
  pageSize = FEATURED_THREADS_PAGE_SIZE,
  isAuthenticated = false,
  config,
  client = csrClient,
}: fetchFeaturedThreadsParams): Promise<FeaturedThreadsResult> => {
  const { locale, language, zone, timezone } = config;
  try {
    const variables: FeaturedSeekMaxThreadsV3QueryVariables = {
      zone: zone || getZone(),
      cursor,
      pageSize,
      timezone,
      locale,
      hasLearningProfile: isAuthenticated,
    };

    logger.info('fetchFeaturedThreads', variables);

    const { data } = await client.query<
      FeaturedSeekMaxThreadsV3Query,
      FeaturedSeekMaxThreadsV3QueryVariables
    >({
      query: FeaturedSeekMaxThreadsV3Document,
      variables,
    });

    return generateFeaturedThreads(data, language as CareerHubLanguage);
  } catch (error) {
    logger.error({ error }, 'fetchFeaturedThreads');
    throw error;
  }
};

export interface fetchCategoryThreadsParams {
  categoryId: string;
  cursor: number;
  hasLearningProfile?: boolean;
  client?: ApolloClient<NormalizedCacheObject>;
  pageSize?: number;
  config: Pick<ConfigStateContext, 'zone' | 'locale' | 'language' | 'timezone'>;
}
export const fetchCategoryThreads = async ({
  categoryId,
  cursor,
  hasLearningProfile = false,
  client = csrClient,
  pageSize = PAGE_SIZE,
  config,
}: fetchCategoryThreadsParams): Promise<CategoryThreadsResult> => {
  const { zone, locale, language, timezone } = config;
  try {
    const variables: CategorySeekMaxThreadsQueryVariables = {
      locale: locale || getLocale(),
      zone: zone || getZone(),
      cursor,
      pageSize,
      timezone,
      hasLearningProfile,
      categoryId,
    };

    logger.info('fetchCategoryThreads', variables);

    const { data } = await client.query<
      CategorySeekMaxThreadsQuery,
      CategorySeekMaxThreadsQueryVariables
    >({
      query: CategorySeekMaxThreadsDocument,
      variables,
    });

    return generateGroupThreads(
      data.categorySeekMaxThreads,
      language as CareerHubLanguage,
    );
  } catch (error) {
    logger.error({ error }, 'fetchCategoryThreads');
    throw error;
  }
};

export interface deleteThreadParams {
  id: string;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const deleteThread = async ({
  id,
  client = csrClient,
}: deleteThreadParams): Promise<
  DeleteSeekMaxThreadMutation['deleteSeekMaxThread']
> => {
  try {
    const variables: DeleteSeekMaxThreadMutationVariables = {
      locale: getLocale(),
      id,
    };

    logger.info('deleteThread', variables);

    const { data } = await client.mutate({
      mutation: DeleteSeekMaxThreadDocument,
      variables,
    });
    const result = data?.deleteSeekMaxThread;
    if (!result) {
      throw new Error('Failed to delete thread');
    }
    if (result.__typename === 'DeleteSeekMaxThreadFailure') {
      throw new Error(result.errors[0].message);
    }
    return result;
  } catch (error) {
    logger.error({ error }, 'deleteThread');
    throw error;
  }
};

export interface CreateThreadParams {
  description: string;
  categoryId: string;
  attachments?:
    | CreateSeekMaxThreadAttachmentInput
    | CreateSeekMaxThreadAttachmentInput[];
  client?: ApolloClient<NormalizedCacheObject>;
  config: Pick<ConfigStateContext, 'locale' | 'timezone' | 'zone'>;
}
export const createThread = async ({
  description,
  categoryId,
  attachments = [], // We're not using attachments for now
  client = csrClient,
  config,
}: CreateThreadParams): Promise<ThreadCreationStatus> => {
  try {
    const { locale, timezone, zone } = config;
    const variables: CreateSeekMaxThreadMutationVariables = {
      locale,
      timezone,
      zone,
      categoryId,
      description,
      attachments,
    };

    logger.info('createThread', variables);

    const { data } = await client.mutate({
      mutation: CreateSeekMaxThreadDocument,
      variables,
    });

    const result = data?.createSeekMaxThreadV2;
    if (!result) {
      throw new ThreadError();
    }
    if (result.__typename === 'CreateSeekMaxThreadFailure') {
      let error: ThreadError;
      switch (result.errors[0].__typename) {
        case 'CreateSeekMaxThreadBlockedWordsError':
          error = new ThreadError('BLACKLISTED_WORDS');
          break;
        case 'CreateSeekMaxThreadExceedRateLimitError':
          error = new ThreadError('RATE_LIMIT_EXCEEDED');
          break;
        case 'CreateSeekMaxThreadUserIsBannedError':
          error = new ThreadError('UNAUTHORIZED_REPLY');
          break;
        default:
          error = new ThreadError();
      }
      throw error;
    }
    return result;
  } catch (error) {
    logger.error({ error }, 'createThread');
    throw error;
  }
};
